import { useSyncExternalStore } from 'react';

import { ClientCore } from 'client-core';

import { useSharedClientCoreSubscriptionContext } from './SharedClientCoreSubscriptionContext';

/**
 * Returns a part of the passed in ClientCore and re-renders the using component if that part
 * changes.
 *
 * It is safe to extract different parts of the ClientCore using multiple instances of
 * useClientCorePart in one component. Only one re-render will take place if multiple changes happen
 * at once.
 */
function useClientCorePart<K extends keyof ClientCore>(
  clientCore: ClientCore,
  key: K,
): ClientCore[K];
function useClientCorePart<K extends keyof ClientCore>(
  clientCore: ClientCore | undefined,
  key: K,
): ClientCore[K] | undefined;

function useClientCorePart<K extends keyof ClientCore>(
  clientCore: ClientCore | undefined,
  key: K,
): ClientCore[K] | undefined {
  return useClientCoreSnapshot(clientCore, () => clientCore?.[key]);
}

/**
 * Returns a snapshot of the passed in ClientCore and re-renders the using component if that
 * snapshot changes.
 *
 * Note that react considers a snapshot changed based on referential identity. Users must take care
 * to return primitives or memoized objects!
 **/
function useClientCoreSnapshot<T>(clientCore: ClientCore, getSnapshot: () => T): T;
function useClientCoreSnapshot<T>(
  clientCore: ClientCore | undefined,
  getSnapshot: () => T,
): T | undefined;

function useClientCoreSnapshot<T>(
  clientCore: ClientCore | undefined,
  getSnapshot: () => T,
): T | undefined {
  const { subscribe } = useSharedClientCoreSubscriptionContext();
  return useSyncExternalStore(
    (onStoreChange) => {
      if (!clientCore) {
        return () => {};
      }
      return subscribe(clientCore, onStoreChange);
    },
    () => getSnapshot(),
  );
}

export default useClientCorePart;
export { useClientCoreSnapshot };
